import {
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	useMultiStyleConfig,
	useBreakpointValue,
	TabsProps,
	TabPanelsProps,
	TabPanelProps,
	chakra,
} from "@chakra-ui/react";
import React, { ReactNode } from "react";
import { HeadingLevelBoundary, Hx } from "src/components/Heading/Heading";
import { createContext } from "src/lib/react";

export type TabsOrColumnsProps = Omit<TabsProps, "children"> & {
	columns: Array<{ id: string; title: string }>;
	children: ReactNode;
};

export const desktopBreakpoint = "md";

const [TabsOrColumnsContext, useTabsOrColumnsContext] = createContext<{
	isTabbed: boolean;
	styles: ReturnType<typeof useMultiStyleConfig>;
}>("TabsOrColumnsContext");

export const TabsOrColumns: React.FC<TabsOrColumnsProps> = ({
	columns,
	children,
	...rest
}) => {
	const isTabbed =
		useBreakpointValue(
			{
				base: true,
				[desktopBreakpoint]: false,
			},
			{ fallback: desktopBreakpoint },
		) ?? false;

	const styles = useMultiStyleConfig("TabsOrColumns", {
		desktopBreakpoint,
		isTabbed,
	});

	return (
		<TabsOrColumnsContext.Provider value={{ isTabbed, styles }}>
			<Tabs variant="tabsOrColumns" {...rest}>
				{children}
			</Tabs>
		</TabsOrColumnsContext.Provider>
	);
};

export const TabsOrColumnsPanels: React.FC<
	TabPanelsProps & {
		children: ReactNode;
		showDivider?: boolean;
		extendDivider?: boolean;
	}
> = ({ children, showDivider = true, extendDivider = false, ...rest }) => {
	const { isTabbed } = useTabsOrColumnsContext();

	const styles = useMultiStyleConfig("TabsOrColumns", {
		desktopBreakpoint,
		showDivider,
		extendDivider,
		isTabbed,
	});

	return (
		<TabPanels sx={styles.columns} {...rest}>
			{children}
		</TabPanels>
	);
};

export const TabsOrColumnsTablist: React.FC<{
	columns: Array<{ id: string; title: string }>;
}> = ({ columns }) => {
	const { styles } = useTabsOrColumnsContext();

	const isMobile = useBreakpointValue(
		{
			base: true,
			md: false,
		},
		{ fallback: "base" },
	);
	if (isMobile) {
		return (
			<TabList sx={styles.tabs}>
				{columns.map((column) => (
					<Tab sx={styles.tab} key={`${column.id}-mobile`}>
						{column.title}
					</Tab>
				))}
			</TabList>
		);
	}

	// the tabs are not interactive on desktop, they are just titles
	return (
		<chakra.span sx={styles.tabs}>
			{columns.map((column) => (
				<HeadingLevelBoundary key={`${column.id}-title`}>
					<Hx size="h3" sx={styles.tab}>
						{column.title}
					</Hx>
				</HeadingLevelBoundary>
			))}
		</chakra.span>
	);
};

export const TabsOrColumnsPanel: React.FC<
	TabPanelProps & { children: ReactNode }
> = ({ children, ...rest }) => {
	const { styles } = useTabsOrColumnsContext();
	return (
		<TabPanel sx={styles.column} paddingTop={99} {...rest}>
			<HeadingLevelBoundary>{children}</HeadingLevelBoundary>
		</TabPanel>
	);
};

// 🔬 e2e
